import React, { useEffect, useState } from 'react';
import { Select, message } from 'antd';
import { toast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import {
  BottomSheet,
  PageLayout,
} from '@/components';
import { ROUTE } from '@/config';
import Step1 from './step1';
import Step2 from './step2';
import Step3 from './step3';
import { useTranslation } from 'react-i18next';
import { useMoveOrderExcelById } from '@/hooksApi/useImport';
import { fetchOrderExcelByUID } from '@/api';
import { PickupTypeEnum, UploadExcelResponse, PermissionName } from '@/types';
import { useCodDropOff } from '@/hooks/useCodDropOff';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useCustomerIO, useFileUpload, useImportTableUid } from '@/hooks';
import usePermission from '@/hooks/usePermission';
import { useFetchUser } from '@/hooksApi';
import { useConfirmModalStore, useBulkImportStore } from '@/stores';
import { ProcessNameEnum, usePollingMechanismStore } from '@/stores/pollingMechanismStore';
import { Upload, Eye, CheckCircle, ArrowLeft } from 'lucide-react';

const Import: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isInitializing, setIsInitializing] = useState(true);
  const [pendingImportToast, setPendingImportToast] = useState<{ dismiss: () => void } | null>(
    null,
  );
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = useState<string>('boxy_collects');
  const [pickupAddressId, setPickupAddressId] = useState<string | undefined>(undefined);
  const [id, setId] = useState<string | null>(null);
  const [row, setRow] = useState<number>(0);
  const [needsRevalidation, setNeedsRevalidation] = useState(false);

  const navigation = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { startPolling } = usePollingMechanismStore();
  const { hasPermission } = usePermission();
  const { getDraft, clearPendingImport } = useBulkImportStore();

  const { mutate: moveOrder, isLoading: isMoveOrderLoading } = useMoveOrderExcelById();
  const { pickupAddresses, pickupAddressLoading } = useCodDropOff();

  const setImportTableUid = useImportTableUid((state) => state.setImportTableUid); // Access the Table UID setter from Zustand

  const { handleBeforeUpload, uploadFile, uploadedFile, resetFile } =
    useFileUpload(onSuccessUpload);

  const { data: userData } = useFetchUser();
  const { openConfirm } = useConfirmModalStore();
  const { trackEvent } = useCustomerIO();

  const listCodDropOff = pickupAddresses?.filter((item) => item.type === PickupTypeEnum.WAREHOUSE);

  const stepContents = [
    <Step1
      key='step1'
      handleBeforeUpload={handleBeforeUpload}
      uploadFile={uploadFile}
      uploadedFile={uploadedFile}
      handleResetUploadedFile={handleResetUploadedFile}
    />,
    <Step2 key='step2' id={id} />,
    <Step3 key='step3' id={id} setRow={setRow} selectedPickupAddressId={pickupAddressId} needsRevalidation={needsRevalidation} onRevalidationStarted={() => setNeedsRevalidation(false)} />,
  ];

  const stepperItems = [
    { label: String(t('upload_file')), icon: <Upload size={14} /> },
    { label: String(t('preview_data')), icon: <Eye size={14} /> },
    { label: String(t('import')), icon: <CheckCircle size={14} /> },
  ];

  const next = () => {
    if (id === null && currentStep === 0) {
      message.error(t('must_upload_file'));
      return;
    }
    // Dismiss pending import toast when moving to next step
    if (pendingImportToast && currentStep === 0) {
      pendingImportToast.dismiss();
      setPendingImportToast(null);
    }
    setCurrentStep((prev) => prev + 1);
  };

  function onSuccessUpload(response: UploadExcelResponse) {
    setId(response?.object?.uid || '');

    setImportTableUid(response?.object?.uid || '');

    message.success(t('upload_Success'));

    setCurrentStep((prev: number) => prev + 1);
  }

  // Handle draft restoration with user confirmation
  const handleContinueDraft = (dismissFn: () => void) => {
    const draft = getDraft();
    if (draft) {
      setId(draft.uuid);
      setImportTableUid(draft.uuid);
      setCurrentStep(draft.step);
      // Refetch data for the restored draft
      queryClient.invalidateQueries(['import', draft.uuid]);
      queryClient.invalidateQueries(['import-valid', draft.uuid]);
      queryClient.invalidateQueries(['import-invalid', draft.uuid]);
      queryClient.invalidateQueries(['import-no-carrier', draft.uuid]);
      // Trigger carrier revalidation in case settings changed while away
      setNeedsRevalidation(true);
      dismissFn();
    }
  };

  const handleDiscardDraft = (dismissFn: () => void) => {
    clearPendingImport();
    dismissFn();
  };

  // Check for pending draft on mount and show confirmation toast
  useEffect(() => {
    const draft = getDraft();
    const autoResume = searchParams.get('autoResume');

    if (draft) {
      // If returning from Carrier Settings with autoResume, directly restore Step 3
      if (autoResume === 'true') {
        setId(draft.uuid);
        setImportTableUid(draft.uuid);
        setCurrentStep(draft.step);
        // Trigger revalidation when Step3 mounts (re-runs carrier matching)
        setNeedsRevalidation(true);
        // Clean up URL parameter
        setSearchParams({});
      } else if (currentStep === 0) {
        // Only show toast on step 1 (currentStep === 0)
        const toastRef = toast({
          title: t('unfinished_bulk_import_question'),
          duration: Infinity,
          actions: [
            <ToastAction
              key='continue'
              altText={t('continue')}
              onClick={() => handleContinueDraft(toastRef.dismiss)}
              className='bg-sd-primary text-sd-primary-foreground hover:bg-sd-primary/90'
            >
              {t('continue')}
            </ToastAction>,
            <ToastAction
              key='discard'
              altText={t('discard')}
              onClick={() => handleDiscardDraft(toastRef.dismiss)}
            >
              {t('discard')}
            </ToastAction>,
          ],
        });
        setPendingImportToast(toastRef);
      }
    }

    // Mark initialization complete
    setIsInitializing(false);

    // Cleanup: dismiss toast when component unmounts (navigating away from page)
    return () => {
      if (pendingImportToast) {
        pendingImportToast.dismiss();
      }
    };
  }, []);

  function handleResetUploadedFile() {
    setId(null);
    resetFile();
  }

  const handleSelect = (value: string) => {
    setSelectedValue(value);
  };

  const handleChange = (value: string) => {
    setPickupAddressId(value);
  };

  const handleSubmit = async () => {
    if (selectedValue === 'boxy_collects' && pickupAddressId === '') {
      message.error(t('pickup_empty'));
      return;
    }
    if (userData?.object.verified_at) {
      // Revalidate carrier data before submission to get fresh process_ids
      // This is a non-blocking call - we log the response and proceed regardless
      try {
        console.log('[Bulk Import] Revalidating carrier data before submission...');
        const revalidatedData = await fetchOrderExcelByUID(id as string, 1, 1000);
        console.log('[Bulk Import] Carrier revalidation response:', {
          total_rows: revalidatedData?.object?.total,
          valid_rows: revalidatedData?.object?.valid_rows_count,
          invalid_rows: revalidatedData?.object?.invalid_rows_count,
          sample_row_process_ids: revalidatedData?.object?.items?.slice(0, 3).map((row: any) => ({
            uid: row.uid,
            process_id: row.process_id,
            recommended_carrier: row.recommended_carrier_slug,
            has_carrier_error: row.has_carrier_error,
          })),
        });
      } catch (error) {
        console.warn('[Bulk Import] Carrier revalidation failed (non-blocking):', error);
        // Continue with submission even if revalidation fails
      }

      // Proceed with normal submission flow
      moveOrder(
        {
          uid: id as string,
          data: {
            pick_up_type: selectedValue === 'boxy_collects' ? 'PICK_UP' : 'DROP_OFF',
            saved_pick_up_address_uid: pickupAddressId,
          },
        },
        {
          onSuccess: () => {
            // Clear pending import UID after successful submission
            clearPendingImport();
            message.success(t('operation'));
            navigation(ROUTE.ORDERS);
            startPolling(
              ProcessNameEnum.BULK_IMPORT,
              {
                fileName: uploadedFile?.name ?? '',
                toastTextKey: 'import_is_in_queue',
              },
              async () => {
                queryClient.invalidateQueries(['orders']);
                return false;
                // throw new Error('Test Error');
              },
              50000,
              50000,
            );
          },
          onError: () => { message.error(t('operation_failed_please_try_again')); },
        },
      );
    } else {
      openNotVerifiedConfirm();
    }
  };

  const openNotVerifiedConfirm = () => {
    openConfirm({
      description: t('user_not_verified'),
      onConfirm: () => {
        trackEvent(
          'request_verification',
          {
            userId: userData?.object?.uid,
            traits: {
              phone: userData?.object?.phone,
              user_type: 'MERCHANT',
              email: userData?.object?.email,
              firstname: userData?.object?.first_name,
              lastname: userData?.object?.last_name,
              fullname: `${userData?.object?.first_name}${userData?.object?.last_name}`,
            },
          },
          t('thank_you_for_choosing_Boxy_we_will_contact_you_soon'),
        );
      },
      approveText: t('request_activation'),
      cancelText: t('cancel'),
    });
  };

  useEffect(() => {
    if (pickupAddresses.length > 0) {
      const warehouseAddresses = pickupAddresses.filter(
        (item) => item.type === PickupTypeEnum.WAREHOUSE,
      );
      const defaultWarehouse = warehouseAddresses.find((item) => item.default);
      if (defaultWarehouse) {
        setPickupAddressId(defaultWarehouse.uid);
      } else if (warehouseAddresses.length > 0) {
        setPickupAddressId(warehouseAddresses[0].uid);
      }
    }
  }, [pickupAddresses]);

  // Prevent step flicker - show nothing until initialization completes
  if (isInitializing) {
    return null;
  }

  const totalSteps = stepContents.length;

  return (
    <PageLayout
      className='min-h-screen'
      bottomSheet={
        <BottomSheet
          sticky
          isVisible={true}
          justify={currentStep === totalSteps - 1 ? 'flex-start' : 'flex-end'}
          wrapperClassName='sd-font !h-auto !max-h-none !p-5 !py-4 !bottom-4 !rounded-xl !border !border-solid !border-[#e8e6e3] !shadow-lg'
        >
          {currentStep === totalSteps - 1 ? (
            <div className='flex w-full items-center justify-between'>
              <div className='flex items-center gap-3'>
                <button
                  type='button'
                  className='h-9 px-4 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
                  onClick={() => setCurrentStep((prev) => prev - 1)}
                >
                  {String(t('back'))}
                </button>
                {selectedValue === 'boxy_collects' && (
                  <div className='flex items-center gap-2'>
                    <span className='text-sm font-semibold text-sd-foreground whitespace-nowrap'>
                      {String(t('pickup_location'))}:
                    </span>
                    <Select
                      style={{ width: 200, height: 36 }}
                      loading={pickupAddressLoading}
                      placeholder={t('pickup_location')}
                      onChange={handleChange}
                      value={pickupAddressId}
                      options={listCodDropOff?.map((item) => ({
                        label: item.title,
                        value: item.uid,
                      }))}
                    />
                  </div>
                )}
              </div>
              <button
                type='button'
                className='flex items-center gap-1.5 h-9 px-5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(230,217,60)] transition-colors'
                onClick={handleSubmit}
                disabled={row > 0 || !hasPermission(PermissionName.BULK_IMPORT_ORDER) || isMoveOrderLoading}
              >
                {isMoveOrderLoading ? (
                  <span className='h-3.5 w-3.5 rounded-full border-2 border-[rgb(57,57,60)] border-t-transparent animate-spin' />
                ) : (
                  <Upload size={14} />
                )}
                {String(t('import_orders'))}
              </button>
            </div>
          ) : (
            <div className='flex w-full justify-between items-center'>
              {currentStep === 0 ? (
                <>
                  <div />
                  <div className='flex items-center gap-3'>
                    <button
                      type='button'
                      className='h-9 px-4 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
                      onClick={() => navigation(ROUTE.ORDERS)}
                    >
                      {String(t('cancel'))}
                    </button>
                    <button
                      type='button'
                      className='flex items-center gap-1.5 h-9 px-5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(230,217,60)] transition-colors'
                      disabled={id === null || !hasPermission(PermissionName.BULK_IMPORT_ORDER)}
                      onClick={next}
                    >
                      <Upload size={14} />
                      {String(t('import_orders'))}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <button
                    type='button'
                    className='h-9 px-4 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors'
                    onClick={() => setCurrentStep((prev) => prev - 1)}
                  >
                    {String(t('back'))}
                  </button>
                  <button
                    type='button'
                    className='flex items-center gap-1.5 h-9 px-5 rounded-lg bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer hover:bg-[rgb(230,217,60)] transition-colors'
                    onClick={next}
                  >
                    <Upload size={14} />
                    {String(t('confirm_import'))}
                  </button>
                </>
              )}
            </div>
          )}
        </BottomSheet>
      }
    >
      <div className={`sd-font mx-auto pb-32 ${currentStep === totalSteps - 1 ? 'w-full' : 'max-w-[720px]'}`}>
        {/* Back link */}
        <button
          onClick={() => navigation(ROUTE.ORDERS)}
          className='flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-2 bg-transparent border-none cursor-pointer p-0'
        >
          <ArrowLeft size={16} style={{ transform: document.dir === 'rtl' ? 'scaleX(-1)' : undefined }} />
          <span>{String(t('back_to_orders'))}</span>
        </button>

        {/* Page title */}
        <h1 className='text-2xl font-bold text-sd-foreground mt-1'>
          {String(t('bulk_import_orders'))}
        </h1>

        {/* Custom stepper */}
        <div className='mt-4 mb-6'>
          {/* Progress bar track */}
          <div className='w-full h-1.5 rounded-full bg-gray-200 mb-2.5'>
            <div
              className='h-full rounded-full bg-sd-primary transition-all duration-700 ease-out'
              style={{ width: `${(currentStep / (stepperItems.length - 1)) * 100}%` }}
            />
          </div>
          {/* Stepper labels — grid for exact center alignment with progress bar */}
          <div className='grid grid-cols-3 w-full'>
            {stepperItems.map((step, index) => (
              <div
                key={index}
                className={`flex items-center gap-1.5 text-xs font-medium ${
                  index <= currentStep ? 'text-sd-foreground' : 'text-gray-500'
                } ${index === 0 ? 'justify-self-start' : index === stepperItems.length - 1 ? 'justify-self-end' : 'justify-self-center'}`}
              >
                {step.icon}
                <span>{step.label}</span>
              </div>
            ))}
          </div>
        </div>

        {stepContents[currentStep]}
      </div>
    </PageLayout>
  );
};

export default Import;
